import type { AU_STATES, AU_TIMEZONES, OrganisationBody, SiteBody } from '@pulsepoint/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, patch, post } from './client';

export interface Organisation {
  id: string;
  accountNumber: string | null;
  businessName: string;
  tradingAs: string | null;
  abnAcn: string | null;
  addressLevel: string | null;
  addressStreet: string | null;
  addressSuburb: string | null;
  addressState: (typeof AU_STATES)[number] | string | null;
  addressPostcode: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  accountsContactName: string | null;
  accountsContactEmail: string | null;
  accountsContactPhone: string | null;
  billingFrequency: string | null;
  accountOpenedOn: string | null;
  timezone: (typeof AU_TIMEZONES)[number] | string;
  notes: string | null;
  /** Powersensor home charging is on: its tabs and report show, and readings are downloaded. */
  powersensorEnabled: boolean;
  /** $/kWh drivers are paid back for charging at home (numeric, as text). */
  powersensorRate: string | null;
  isActive: boolean;
  siteCount: number;
  chargerCount: number;
  driverCount: number;
}

export interface Site {
  id: string;
  organisationId: string;
  organisationName: string;
  name: string;
  addressStreet: string | null;
  addressSuburb: string | null;
  addressState: string | null;
  addressPostcode: string | null;
  latitude: string | null;
  longitude: string | null;
  timezone: string;
  /** Peak and off-peak hours for prices here; null: one price all day. */
  rateScheduleId: string | null;
  rateScheduleName: string | null;
  notes: string | null;
  isActive: boolean;
  chargerCount: number;
}

const orgKey = ['organisations'] as const;
const siteKey = ['sites'] as const;

export const useOrganisations = (include: 'active' | 'all' = 'active') =>
  useQuery({ queryKey: [...orgKey, include], queryFn: () => get<Organisation[]>(`/organisations?include=${include}`) });

export const useSites = (organisationId: string | undefined, include: 'active' | 'all' = 'active') =>
  useQuery({
    queryKey: [...siteKey, organisationId ?? 'all', include],
    queryFn: () => get<Site[]>(`/sites?include=${include}${organisationId ? `&organisationId=${organisationId}` : ''}`),
  });

/** Changes to sites also change the organisation's counts, so both refresh. */
function useInvalidating<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgKey });
      void qc.invalidateQueries({ queryKey: siteKey });
    },
  });
}

export const useCreateOrganisation = () => useInvalidating((body: OrganisationBody) => post<Organisation>('/organisations', body));
export const useUpdateOrganisation = () =>
  useInvalidating(({ id, ...body }: Partial<OrganisationBody> & { id: string; isActive?: boolean }) => patch<Organisation>(`/organisations/${id}`, body));
export const useDeactivateOrganisation = () => useInvalidating((id: string) => del<Organisation>(`/organisations/${id}`));

export const useCreateSite = () => useInvalidating((body: SiteBody & { organisationId: string }) => post<Site>('/sites', body));
export const useUpdateSite = () => useInvalidating(({ id, ...body }: Partial<SiteBody> & { id: string; isActive?: boolean }) => patch<Site>(`/sites/${id}`, body));
export const useDeactivateSite = () => useInvalidating((id: string) => del<Site>(`/sites/${id}`));
